import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Building2, CreditCard, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useUserProfile } from "@/hooks/use-user-profile";
import { useUserRole } from "@/hooks/use-user-role";
import AuthDebugPanel from "@/components/auth/AuthDebugPanel";

const NoOrganizationGuard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const { data: userRole } = useUserRole();

  return (
    <div className="space-y-6">
      <div className="min-h-[60vh] flex items-center justify-center">
      <Card className="w-full max-w-2xl border-2">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Info className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Organization Required</CardTitle>
          <CardDescription className="text-base mt-2">
            You need to be part of an organization to access this section of ZooCertica
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              To access full admin features, you must either create a new organization or join an existing one.
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            <Button 
              onClick={() => navigate('/waiting-for-invitation')} 
              className="w-full"
              size="lg"
              variant="default"
            >
              <Building2 className="w-4 h-4 mr-2" />
              View Organization Options
            </Button>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Available while waiting
                </span>
              </div>
            </div>

            <div className="grid gap-3">
              <Button 
                variant="outline" 
                onClick={() => navigate('/admin/billing')}
                className="justify-start"
              >
                View Billing Information
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/legal-lookup')}
                className="justify-start"
              >
                Access Legal Lookup Tool
              </Button>
            </div>
          </div>

          <div className="text-center text-sm text-muted-foreground space-y-2">
            <p>
              If you're expecting an invitation, check your email or contact your organization administrator.
            </p>
            {/* Debug Info - Hidden but kept for future debugging
            <div className="text-xs text-muted-foreground space-y-1 p-2 bg-muted rounded">
              <p><strong>Debug Info:</strong></p>
              <p>User: {user?.email}</p>
              <p>Profile ID: {(profile as any)?.id || 'None'}</p>
              <p>Organization ID: {(profile as any)?.organization_id || 'None'}</p>
              <p>Role: {userRole || 'None'}</p>
            </div>
            */}
          </div>
        </CardContent>
      </Card>
      </div>
      {/* <AuthDebugPanel /> - Hidden but kept for future debugging */}
    </div>
  );
};

export default NoOrganizationGuard;